import { Platform } from 'quasar';
import { BranchDeepLinks } from 'capacitor-branch-deep-links';

import * as formatUtils from 'src/utils/format';

import type * as gtmTypes from 'src/types/gtm';

/*
 * @note Unified tracking service. On Capacitor we send Branch events; on web we push
 * to GTM's window.dataLayer (prod only). Each event function inlines both platforms.
 */

function shouldUseBranch(): boolean {
  return Platform.is.capacitor;
}

function pushToDataLayer(event: gtmTypes.GTMEvent & Record<string, unknown>): void {
  if (shouldUseBranch()) return;
  if (process.env.NODE_ENV === 'production' && window.dataLayer) {
    window.dataLayer.push(event);
  }
}

export async function logout(): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.logout();
      console.log('[tracking] User logged out');
    } catch (error) {
      console.error('[tracking] Error logging out:', error);
    }
    return;
  }

  pushToDataLayer({ event: 'Logout' });
}

export async function setUserIdentity(params: { userId: string }): Promise<void> {
  if (!shouldUseBranch()) return;

  try {
    await BranchDeepLinks.setIdentity({ newIdentity: params.userId });
    console.log('[tracking] User identity set:', params.userId);
  } catch (error) {
    console.error('[tracking] Error setting user identity:', error);
  }
}

export async function trackAddPaymentInfo(params: {
  paymentMethod: string;
  amount: number;
  currency: string;
  egoOrderId?: string;
}): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.sendBranchEvent({
        eventName: 'ADD_PAYMENT_INFO',
        metaData: {
          revenue: params.amount,
          currency: params.currency,
          description: 'User selected payment method in checkout',
          customData: {
            payment_method: params.paymentMethod,
            ...(params.egoOrderId && { ego_order_id: params.egoOrderId }),
            checkout_step: 'payment_checkout',
          },
        },
      });
      console.log('[tracking] ADD_PAYMENT_INFO tracked:', params.paymentMethod);
    } catch (error) {
      console.error('[tracking] Error tracking add payment info:', error);
    }
    return;
  }

  pushToDataLayer({
    event: 'AddPaymentInfo',
    payment_method: params.paymentMethod,
    value: params.amount,
    currency: params.currency,
    ...(params.egoOrderId && { ego_order_id: params.egoOrderId }),
  });
}

export async function trackCompleteRegistration(params: {
  userId: string;
  email: string;
  dialCode: string;
  phone: string;
}): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.sendBranchEvent({
        eventName: 'COMPLETE_REGISTRATION',
        metaData: {
          description: 'User completed registration',
          customData: {
            registration_method: 'email',
            user_id: params.userId,
            email: params.email,
            dial_code: params.dialCode,
            phone: params.phone,
          },
        },
      });
      console.log('[tracking] COMPLETE_REGISTRATION tracked');
    } catch (error) {
      console.error('[tracking] Error tracking registration:', error);
    }
    return;
  }

  // @note Hash PII client-side. Ad platforms require SHA-256 of normalized
  // (lowercased, trimmed) email and E.164-digits phone.
  const [emailHash, phoneHash] = await Promise.all([
    formatUtils.sha256Hex(params.email),
    formatUtils.sha256Hex(formatUtils.normalizePhone(params.dialCode, params.phone)),
  ]);

  pushToDataLayer({
    event: 'CompleteRegistration',
    user_id: params.userId,
    registration_method: 'email',
    user_data: {
      em: emailHash,
      ph: phoneHash,
    },
  });
}

export async function trackInitiatePurchase(params: {
  bookingHash: string;
  hotelId?: string;
  hotelName?: string;
  roomId?: string;
  roomName?: string;
  amount: number;
  currency: string;
  checkIn?: string;
  checkOut?: string;
  rooms?: number;
  operationFor: string;
}): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.sendBranchEvent({
        eventName: 'INITIATE_PURCHASE',
        metaData: {
          revenue: params.amount,
          currency: params.currency,
          description: 'User initiated hotel booking',
          customData: {
            book_hash: params.bookingHash,
            ...(params.hotelId && { hotel_id: params.hotelId }),
            ...(params.hotelName && { hotel_name: params.hotelName }),
            ...(params.roomId && { room_id: params.roomId }),
            ...(params.roomName && { room_name: params.roomName }),
            ...(params.checkIn && { check_in: params.checkIn }),
            ...(params.checkOut && { check_out: params.checkOut }),
            ...(params.rooms !== undefined && { rooms: params.rooms }),
            checkout_step: 'book_api_call',
          },
        },
      });
      console.log('[tracking] INITIATE_PURCHASE tracked:', params.amount);
    } catch (error) {
      console.error('[tracking] Error tracking initiate purchase:', error);
    }
    return;
  }

  pushToDataLayer({
    event: 'InitiatePurchase',
    value: params.amount,
    currency: params.currency,
    operation_for: params.operationFor,
  });
}

export async function trackLogin(params: { userId: string }): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.sendBranchEvent({
        eventName: 'LOGIN',
        metaData: {
          description: 'User logged in',
          customData: {
            login_method: 'email',
            user_id: params.userId,
          },
        },
      });
      console.log('[tracking] LOGIN tracked');
    } catch (error) {
      console.error('[tracking] Error tracking login:', error);
    }
    return;
  }

  pushToDataLayer({ event: 'Login', user_id: params.userId });
}

export function trackPageView(params: { path: string }): void {
  if (shouldUseBranch()) return;
  pushToDataLayer({ event: 'Page View', page_path: params.path });
}

// @note PURCHASE is the only event that carries actual revenue — used for ROAS tracking.
export async function trackPurchase(params: {
  transactionId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  operationFor: string;
  bookingHash?: string;
  hotelId?: string;
  hotelName?: string;
}): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.sendBranchEvent({
        eventName: 'PURCHASE',
        metaData: {
          transactionID: params.transactionId,
          revenue: params.amount,
          currency: params.currency,
          description: 'User completed hotel booking payment',
          customData: {
            payment_method: params.paymentMethod,
            ...(params.bookingHash && { book_hash: params.bookingHash }),
            ...(params.hotelId && { hotel_id: params.hotelId }),
            ...(params.hotelName && { hotel_name: params.hotelName }),
            purchase_type: 'hotel_booking',
          },
        },
      });
      console.log('[tracking] PURCHASE tracked:', params.amount, '-', params.transactionId);
    } catch (error) {
      console.error('[tracking] Error tracking purchase:', error);
    }
    return;
  }

  pushToDataLayer({
    event: 'Purchase',
    transaction_id: params.transactionId,
    value: params.amount,
    currency: params.currency,
    payment_method: params.paymentMethod,
    operation_for: params.operationFor,
  });
}

export async function trackSearch(params: {
  regionId: string;
  checkIn: string;
  checkOut: string;
  rooms: number;
  currency: string;
  residency: string;
  resultCount?: number;
}): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.sendBranchEvent({
        eventName: 'SEARCH',
        metaData: {
          description: 'User searched for hotels',
          customData: {
            search_query: params.regionId,
            region_id: params.regionId,
            check_in: params.checkIn,
            check_out: params.checkOut,
            rooms: params.rooms,
            currency: params.currency,
            residency: params.residency,
            content_type: 'hotel',
            ...(params.resultCount !== undefined && { result_count: params.resultCount }),
          },
        },
      });
      console.log('[tracking] SEARCH tracked:', params.regionId);
    } catch (error) {
      console.error('[tracking] Error tracking search:', error);
    }
    return;
  }

  pushToDataLayer({
    event: 'Search',
    search_query: params.regionId,
    region_id: params.regionId,
    check_in: params.checkIn,
    check_out: params.checkOut,
    rooms: params.rooms,
    currency: params.currency,
    residency: params.residency,
    ...(params.resultCount !== undefined && { result_count: params.resultCount }),
  });
}

export async function trackViewItem(params: {
  hotelId: string;
  hotelName: string;
  roomId: string;
  roomName: string;
  price?: number;
  checkIn?: string;
  checkOut?: string;
}): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.sendBranchEvent({
        eventName: 'VIEW_ITEM',
        metaData: {
          description: 'User selected a room',
          customData: {
            item_id: params.roomId,
            item_name: params.roomName,
            hotel_id: params.hotelId,
            hotel_name: params.hotelName,
            content_type: 'room',
            ...(params.price !== undefined && { price: params.price }),
            ...(params.checkIn && { check_in: params.checkIn }),
            ...(params.checkOut && { check_out: params.checkOut }),
          },
        },
      });
      console.log('[tracking] VIEW_ITEM tracked:', params.roomName);
    } catch (error) {
      console.error('[tracking] Error tracking view item:', error);
    }
    return;
  }

  pushToDataLayer({
    event: 'ViewItem',
    item_id: params.roomId,
    item_name: params.roomName,
    hotel_id: params.hotelId,
    hotel_name: params.hotelName,
    ...(params.price !== undefined && { value: params.price }),
    ...(params.checkIn && { check_in: params.checkIn }),
    ...(params.checkOut && { check_out: params.checkOut }),
  });
}

export async function trackViewItems(params: {
  hotelId: string;
  hotelName: string;
  roomCount?: number;
  checkIn?: string;
  checkOut?: string;
}): Promise<void> {
  if (shouldUseBranch()) {
    try {
      await BranchDeepLinks.sendBranchEvent({
        eventName: 'VIEW_ITEMS',
        metaData: {
          description: 'User viewed hotel details with rooms list',
          customData: {
            hotel_id: params.hotelId,
            hotel_name: params.hotelName,
            content_type: 'hotel_rooms',
            ...(params.roomCount !== undefined && { room_count: params.roomCount }),
            ...(params.checkIn && { check_in: params.checkIn }),
            ...(params.checkOut && { check_out: params.checkOut }),
          },
        },
      });
      console.log('[tracking] VIEW_ITEMS tracked:', params.hotelName);
    } catch (error) {
      console.error('[tracking] Error tracking view items:', error);
    }
    return;
  }

  pushToDataLayer({
    event: 'ViewItems',
    hotel_id: params.hotelId,
    hotel_name: params.hotelName,
    ...(params.roomCount !== undefined && { room_count: params.roomCount }),
    ...(params.checkIn && { check_in: params.checkIn }),
    ...(params.checkOut && { check_out: params.checkOut }),
  });
}
